import { FormEvent, KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ManualIssueSeverity,
  ManualIssueStatus,
  ManualIssueType,
  ManualIssueWcagVersion,
} from '@accestra/types';
import { filterWcagCriteria, getWcagCriterionByLabel, WCAG_VERSION_OPTIONS } from '../../lib/wcag-criteria';
import Button from '../ui/Button';

const MODAL_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container: HTMLElement | null): HTMLElement[] {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE_SELECTOR)).filter((element) => {
    if (element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    if (element instanceof HTMLInputElement && element.type === 'hidden') {
      return false;
    }

    return element.getClientRects().length > 0;
  });
}

export interface ManualIssueSiteOption {
  id: string;
  name: string;
  baseUrl: string;
}

export interface ManualIssueModalSubmitInput {
  siteId: string;
  issueType: ManualIssueType;
  title: string;
  description: string;
  affectedPageUrl: string | null;
  wcagVersion: ManualIssueWcagVersion | null;
  wcagCriterion: string | null;
  recommendedFix: string | null;
  reviewBatch: string | null;
  severity: ManualIssueSeverity;
  status: ManualIssueStatus;
}

export interface ManualIssueModalInitialValues extends ManualIssueModalSubmitInput {}

type CreateSubmitIntent = 'add-another' | 'done';

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function criterionOptionId(id: string): string {
  return `acc-wcag-criterion-option-${id.replace(/\./g, '-')}`;
}

interface ManualIssueModalProps {
  open: boolean;
  onClose(): void;
  onSubmit(input: ManualIssueModalSubmitInput): Promise<boolean>;
  submitting: boolean;
  error: string | null;
  successMessage?: string | null;
  successSignal?: number;
  siteOptions: ManualIssueSiteOption[];
  mode?: 'create' | 'edit';
  initialValues?: ManualIssueModalInitialValues | null;
  fixedSiteId?: string | null;
  defaultSiteId?: string | null;
  resetSignal?: number;
}

export default function ManualIssueModal({
  open,
  onClose,
  onSubmit,
  submitting,
  error,
  successMessage = null,
  successSignal = 0,
  siteOptions,
  mode = 'create',
  initialValues = null,
  fixedSiteId = null,
  defaultSiteId = null,
  resetSignal = 0,
}: ManualIssueModalProps) {
  const { t } = useTranslation(['common']);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const affectedPageUrlInputRef = useRef<HTMLInputElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const submitIntentRef = useRef<CreateSubmitIntent>('add-another');
  const [siteId, setSiteId] = useState(fixedSiteId ?? defaultSiteId ?? siteOptions[0]?.id ?? '');
  const [issueType, setIssueType] = useState<ManualIssueType>('wcag_issue');
  const [affectedPageUrl, setAffectedPageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [wcagVersion, setWcagVersion] = useState<ManualIssueWcagVersion | ''>('');
  const [wcagCriterion, setWcagCriterion] = useState('');
  const [criterionInputValue, setCriterionInputValue] = useState('');
  const [showCriterionList, setShowCriterionList] = useState(false);
  const [activeCriterionIndex, setActiveCriterionIndex] = useState(0);
  const [description, setDescription] = useState('');
  const [recommendedFix, setRecommendedFix] = useState('');
  const [reviewBatch, setReviewBatch] = useState('');
  const [severity, setSeverity] = useState<ManualIssueSeverity>('serious');
  const [status, setStatus] = useState<ManualIssueStatus>('open');

  const siteLabel = useMemo(() => {
    const selectedSite = siteOptions.find((candidate) => candidate.id === (fixedSiteId ?? siteId));
    return selectedSite ? `${selectedSite.name} · ${selectedSite.baseUrl}` : null;
  }, [fixedSiteId, siteId, siteOptions]);

  const filteredCriteria = useMemo(() => {
    return filterWcagCriteria(criterionInputValue, wcagVersion || null, 8);
  }, [criterionInputValue, wcagVersion]);

  const activeCriterion = filteredCriteria[activeCriterionIndex] ?? null;
  const selectedCriterion = useMemo(() => (
    getWcagCriterionByLabel(criterionInputValue)
  ), [criterionInputValue]);

  useEffect(() => {
    setActiveCriterionIndex((current) => Math.min(current, Math.max(filteredCriteria.length - 1, 0)));
  }, [filteredCriteria.length]);

  useEffect(() => {
    if (!open) {
      return;
    }

    submitIntentRef.current = 'add-another';

    setSiteId(initialValues?.siteId ?? fixedSiteId ?? defaultSiteId ?? siteOptions[0]?.id ?? '');
    setIssueType(initialValues?.issueType ?? 'wcag_issue');
    setAffectedPageUrl(initialValues?.affectedPageUrl ?? '');
    setTitle(initialValues?.title ?? '');
    setWcagVersion(initialValues?.wcagVersion ?? '');
    setWcagCriterion(initialValues?.wcagCriterion ?? '');
    setCriterionInputValue(initialValues?.wcagCriterion ?? '');
    setShowCriterionList(false);
    setActiveCriterionIndex(0);
    setDescription(initialValues?.description ?? '');
    setRecommendedFix(initialValues?.recommendedFix ?? '');
    setReviewBatch(initialValues?.reviewBatch ?? '');
    setSeverity(initialValues?.severity ?? 'serious');
    setStatus(initialValues?.status ?? 'open');

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement !== document.body) {
      lastFocusedElementRef.current = activeElement;
    }

    const frame = window.requestAnimationFrame(() => {
      affectedPageUrlInputRef.current?.focus();
      affectedPageUrlInputRef.current?.select();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [
    defaultSiteId,
    fixedSiteId,
    initialValues?.affectedPageUrl,
    initialValues?.description,
    initialValues?.issueType,
    initialValues?.recommendedFix,
    initialValues?.reviewBatch,
    initialValues?.severity,
    initialValues?.siteId,
    initialValues?.status,
    initialValues?.title,
    initialValues?.wcagCriterion,
    initialValues?.wcagVersion,
    open,
  ]);

  useEffect(() => {
    if (!open || fixedSiteId || siteId || !siteOptions[0]?.id) {
      return;
    }

    setSiteId(siteOptions[0].id);
  }, [fixedSiteId, open, siteId, siteOptions]);

  useEffect(() => {
    if (!open || mode !== 'create' || resetSignal <= 0) {
      return;
    }

    setAffectedPageUrl('');
    setTitle('');
    setWcagVersion('');
    setWcagCriterion('');
    setCriterionInputValue('');
    setShowCriterionList(false);
    setActiveCriterionIndex(0);
    setDescription('');
    setRecommendedFix('');
    setReviewBatch('');
    setIssueType('wcag_issue');
    setSeverity('serious');
    setStatus('open');

    const frame = window.requestAnimationFrame(() => {
      affectedPageUrlInputRef.current?.focus();
      affectedPageUrlInputRef.current?.select();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [mode, open, resetSignal]);

  if (!open) {
    return null;
  }

  const handleClose = () => {
    const focusTarget = lastFocusedElementRef.current;
    onClose();

    if (!focusTarget) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (focusTarget.isConnected) {
        focusTarget.focus();
      }
    });
  };

  const handleModalKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = getFocusableElements(modalRef.current);
    if (focusableElements.length === 0) {
      event.preventDefault();
      modalRef.current?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;

    if (event.shiftKey) {
      if (!activeElement || activeElement === firstElement || !modalRef.current?.contains(activeElement)) {
        event.preventDefault();
        lastElement.focus();
      }
      return;
    }

    if (!activeElement || activeElement === lastElement || !modalRef.current?.contains(activeElement)) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleCriterionInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (filteredCriteria.length === 0) {
        return;
      }
      setShowCriterionList(true);
      setActiveCriterionIndex((current) => (
        showCriterionList ? Math.min(current + 1, filteredCriteria.length - 1) : 0
      ));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (filteredCriteria.length === 0) {
        return;
      }
      setShowCriterionList(true);
      setActiveCriterionIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter' && showCriterionList && activeCriterion) {
      event.preventDefault();
      setWcagCriterion(activeCriterion.label);
      setCriterionInputValue(activeCriterion.label);
      setShowCriterionList(false);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setShowCriterionList(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const succeeded = await onSubmit({
      siteId: fixedSiteId ?? siteId,
      issueType,
      title,
      description,
      affectedPageUrl: emptyToNull(affectedPageUrl),
      wcagVersion: wcagVersion || null,
      wcagCriterion: emptyToNull(wcagCriterion || criterionInputValue),
      recommendedFix: emptyToNull(recommendedFix),
      reviewBatch: emptyToNull(reviewBatch),
      severity,
      status,
    });

    if (succeeded && mode === 'create' && submitIntentRef.current === 'done') {
      handleClose();
    }
  };

  return (
    <div
      className="acc-form-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="acc-manual-issue-title"
      aria-describedby="acc-manual-issue-description"
    >
      <button
        type="button"
        className="acc-form-modal__backdrop"
        onClick={handleClose}
        aria-label={t('manualIssues.closeModal')}
      />

      <div
        ref={modalRef}
        className="acc-form-modal__panel"
        onKeyDown={handleModalKeyDown}
      >
        <div className="acc-form-modal__eyebrow">{t('manualIssues.eyebrow')}</div>
        <h2 id="acc-manual-issue-title" className="acc-form-modal__title">
          {t(mode === 'edit' ? 'manualIssues.editTitle' : 'manualIssues.createTitle')}
        </h2>
        <p id="acc-manual-issue-description" className="acc-form-modal__description">
          {t(
            mode === 'edit'
              ? 'manualIssues.editDescription'
              : fixedSiteId
                ? 'manualIssues.createDescriptionSite'
                : 'manualIssues.createDescriptionGlobal',
          )}
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 14 }}>
          {fixedSiteId ? (
            <div className="acc-form-modal__hint">
              {siteLabel}
            </div>
          ) : (
            <label className="acc-form-modal__field">
              <span className="acc-form-modal__label">{t('manualIssues.siteLabel')}</span>
              <select
                value={siteId}
                onChange={(event) => setSiteId(event.target.value)}
                className="acc-select"
                required
              >
                {siteOptions.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('manualIssues.affectedPageUrlLabel')}</span>
            <input
              ref={affectedPageUrlInputRef}
              className="acc-input"
              value={affectedPageUrl}
              onChange={(event) => setAffectedPageUrl(event.target.value)}
              placeholder={t('manualIssues.affectedPageUrlPlaceholder')}
              maxLength={2000}
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {t('manualIssues.affectedPageUrlHint')}
            </span>
          </label>

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('manualIssues.titleLabel')}</span>
            <input
              ref={titleInputRef}
              className="acc-input"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('manualIssues.titlePlaceholder')}
              maxLength={240}
              required
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <label className="acc-form-modal__field">
              <span className="acc-form-modal__label">{t('manualIssues.wcagVersionLabel')}</span>
              <select
                value={wcagVersion}
                onChange={(event) => {
                  setWcagVersion(event.target.value as ManualIssueWcagVersion | '');
                  setActiveCriterionIndex(0);
                }}
                className="acc-select"
              >
                <option value="">{t('manualIssues.wcagVersionPlaceholder')}</option>
                {WCAG_VERSION_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>

            <div className="acc-form-modal__field" style={{ position: 'relative' }}>
              <label className="acc-form-modal__label" htmlFor="acc-wcag-criterion-input">
                {t('manualIssues.wcagCriterionLabel')}
              </label>
              <input
                id="acc-wcag-criterion-input"
                className="acc-input"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showCriterionList}
                aria-controls="acc-wcag-criterion-listbox"
                aria-activedescendant={showCriterionList && activeCriterion ? criterionOptionId(activeCriterion.id) : undefined}
                value={criterionInputValue}
                onChange={(event) => {
                  setCriterionInputValue(event.target.value);
                  setWcagCriterion(event.target.value);
                  setShowCriterionList(true);
                  setActiveCriterionIndex(0);
                }}
                onFocus={() => setShowCriterionList(true)}
                onBlur={() => window.setTimeout(() => setShowCriterionList(false), 120)}
                onKeyDown={handleCriterionInputKeyDown}
                placeholder={t('manualIssues.wcagCriterionPlaceholder')}
                maxLength={160}
              />
              {showCriterionList && (
                <div
                  id="acc-wcag-criterion-listbox"
                  role="listbox"
                  style={{
                    position: 'absolute',
                    zIndex: 240,
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    maxHeight: 238,
                    overflowY: 'auto',
                    border: '1px solid var(--acc-color-border)',
                    borderRadius: 12,
                    background: 'var(--card-bg)',
                    boxShadow: 'var(--acc-shadow-lg)',
                  }}
                >
                  {filteredCriteria.length === 0 ? (
                    <div style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 13 }}>
                      {t('manualIssues.wcagCriterionNoResults')}
                    </div>
                  ) : filteredCriteria.map((criterion, index) => (
                    <button
                      key={criterion.id}
                      id={criterionOptionId(criterion.id)}
                      type="button"
                      role="option"
                      aria-selected={index === activeCriterionIndex}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setWcagCriterion(criterion.label);
                        setCriterionInputValue(criterion.label);
                        setShowCriterionList(false);
                      }}
                      style={{
                        width: '100%',
                        border: 0,
                        padding: '10px 12px',
                        background: index === activeCriterionIndex ? 'var(--surface-secondary)' : 'transparent',
                        color: 'var(--text)',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                      className="acc-wcag-criterion-option"
                    >
                      <span className="acc-wcag-criterion-option__label">{criterion.label}</span>
                      <span
                        className="acc-wcag-level-badge"
                        aria-label={`Level ${criterion.level}`}
                      >
                        {criterion.level}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {selectedCriterion && (
                <span
                  className="acc-wcag-criterion-selected-level"
                  aria-label={`Selected WCAG criterion conformance level ${selectedCriterion.level}`}
                >
                  Level {selectedCriterion.level}
                </span>
              )}
              <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {t('manualIssues.wcagCriterionHint')}
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <label className="acc-form-modal__field">
              <span className="acc-form-modal__label">{t('manualIssues.severityLabel')}</span>
              <select
                value={severity}
                onChange={(event) => setSeverity(event.target.value as ManualIssueSeverity)}
                className="acc-select"
              >
                <option value="critical">{t('manualIssues.severity.critical')}</option>
                <option value="serious">{t('manualIssues.severity.serious')}</option>
                <option value="moderate">{t('manualIssues.severity.moderate')}</option>
                <option value="minor">{t('manualIssues.severity.minor')}</option>
              </select>
            </label>
          </div>

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('manualIssues.descriptionLabel')}</span>
            <textarea
              className="acc-input"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('manualIssues.descriptionPlaceholder')}
              rows={5}
              maxLength={10000}
              required
              style={{ minHeight: 120, resize: 'vertical' }}
            />
          </label>

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('manualIssues.recommendedFixLabel')}</span>
            <textarea
              className="acc-input"
              value={recommendedFix}
              onChange={(event) => setRecommendedFix(event.target.value)}
              placeholder={t('manualIssues.recommendedFixPlaceholder')}
              rows={4}
              maxLength={10000}
              style={{ minHeight: 104, resize: 'vertical' }}
            />
          </label>

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('manualIssues.reviewBatchLabel')}</span>
            <input
              className="acc-input"
              value={reviewBatch}
              onChange={(event) => setReviewBatch(event.target.value)}
              placeholder={t('manualIssues.reviewBatchPlaceholder')}
              maxLength={240}
            />
            <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {t('manualIssues.reviewBatchHint')}
            </span>
          </label>

          <label className="acc-form-modal__field">
            <span className="acc-form-modal__label">{t('manualIssues.statusLabel')}</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as ManualIssueStatus)}
              className="acc-select"
            >
              <option value="open">{t('manualIssues.status.open')}</option>
              <option value="in_progress">{t('manualIssues.status.in_progress')}</option>
              <option value="resolved">{t('manualIssues.status.resolved')}</option>
            </select>
          </label>

          <label
            className="acc-form-modal__field"
            style={{
              gap: 10,
              padding: '12px 14px',
              border: '1px solid var(--border)',
              borderRadius: 14,
              background: 'var(--surface-secondary)',
            }}
          >
            <span style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={issueType === 'usability_issue'}
                onChange={(event) => setIssueType(event.target.checked ? 'usability_issue' : 'wcag_issue')}
                aria-describedby="acc-manual-issue-type-hint"
                style={{ marginTop: 2 }}
              />
              <span style={{ minWidth: 0 }}>
                <span className="acc-form-modal__label" style={{ marginBottom: 4 }}>
                  {t('manualIssues.usabilityIssueLabel')}
                </span>
                <span
                  id="acc-manual-issue-type-hint"
                  style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}
                >
                  {t('manualIssues.usabilityIssueHint')}
                </span>
              </span>
            </span>
          </label>

          {successMessage && mode === 'create' && successSignal > 0 && (
            <div
              key={successSignal}
              role="status"
              aria-live="polite"
              style={{
                color: 'var(--acc-color-success)',
                fontSize: 13,
                padding: '10px 12px',
                borderRadius: 12,
                border: '1px solid color-mix(in srgb, var(--acc-color-success) 32%, transparent)',
                background: 'color-mix(in srgb, var(--acc-color-success) 12%, var(--card-bg))',
              }}
            >
              {successMessage}
            </div>
          )}

          {error && (
            <div role="alert" style={{ color: 'var(--acc-color-danger)', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div className="acc-form-modal__actions">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
              {t('manualIssues.cancel')}
            </Button>
            {mode === 'create' ? (
              <>
                <Button
                  type="submit"
                  variant="secondary"
                  disabled={submitting}
                  onClick={() => {
                    submitIntentRef.current = 'done';
                  }}
                >
                  {submitting && submitIntentRef.current === 'done'
                    ? t('actions.saving')
                    : t('manualIssues.doneAction')}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  onClick={() => {
                    submitIntentRef.current = 'add-another';
                  }}
                >
                  {submitting && submitIntentRef.current === 'add-another'
                    ? t('manualIssues.creating')
                    : t('manualIssues.addAnotherAction')}
                </Button>
              </>
            ) : (
              <Button type="submit" disabled={submitting}>
                {submitting ? t('actions.saving') : t('actions.save')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
